/**
 * Mensagem de WhatsApp enviada aos catequizandos faltosos.
 *
 * O texto é editável pelo catequista e guardado no `localStorage` do navegador — **um template só
 * para todas as turmas**. Suporta duas famílias de marcadores:
 *
 * - **Variáveis:** `{primeiroNome}`, `{nome}`, `{turma}`, `{faltas}`, `{ultimaData}`.
 * - **Flexão de gênero:** `{masculino|feminino}` — ex.: `sozinh{o|a}`, `querid{o|a}`, `{ele|ela}`.
 *   Resolve pelo `sexo` da pessoa (`M`/`F`); **sem sexo cadastrado**, cai para a forma com
 *   parênteses (`sozinho(a)`), que é o comportamento antigo.
 */

const LS_CHAVE = 'igreja_msg_faltas_v1';

/** Texto padrão — escrito sem flexão de gênero, para funcionar mesmo sem o sexo cadastrado. */
export const MENSAGEM_FALTAS_PADRAO = `Olá, {primeiroNome}! Tudo bem?

Aqui é da catequese — {turma}. Sentimos sua falta nos nossos encontros. Sua presença faz diferença e enriquece o nosso caminho na fé.

Saiba que você não está só. Se estiver passando por alguma coisa, ou se precisar de ajuda para voltar, é só falar com a gente — pode contar conosco.

Esperamos por você no próximo encontro!`;

export interface ContextoMensagemFaltas {
  nome?: string;
  /** `M`, `F` ou vazio/desconhecido. */
  sexo?: string | null;
  turma?: string;
  faltas?: number;
  /** Já formatada para exibição (dd/MM/yyyy) ou vazio. */
  ultimaData?: string;
}

export const TOKENS_MENSAGEM: ReadonlyArray<{ token: string; descricao: string }> = [
  { token: '{primeiroNome}', descricao: 'Primeiro nome do catequizando' },
  { token: '{nome}', descricao: 'Nome completo' },
  { token: '{turma}', descricao: 'Nome da turma' },
  { token: '{faltas}', descricao: 'Total de faltas' },
  { token: '{ultimaData}', descricao: 'Data da última frequência' },
  { token: '{o|a}', descricao: 'Flexão de gênero (ex.: sozinh{o|a})' },
];

export function carregarMensagemFaltas(): string {
  try {
    const salvo = localStorage.getItem(LS_CHAVE);
    return salvo != null && salvo.trim() !== '' ? salvo : MENSAGEM_FALTAS_PADRAO;
  } catch {
    // localStorage indisponível (modo privado/bloqueado): segue com o padrão.
    return MENSAGEM_FALTAS_PADRAO;
  }
}

export function salvarMensagemFaltas(texto: string): boolean {
  try {
    localStorage.setItem(LS_CHAVE, texto);
    return true;
  } catch {
    return false;
  }
}

export function limparMensagemFaltas(): void {
  try {
    localStorage.removeItem(LS_CHAVE);
  } catch {
    /* nada a fazer */
  }
}

function primeiroNome(nomeCompleto: string | undefined): string {
  const n = (nomeCompleto ?? '').trim();
  if (!n) return '';
  return n.split(/\s+/)[0] ?? '';
}

/** `M` / `F` a partir do que o cadastro tiver ('M', 'F', 'MASCULINO', 'FEMININO'). */
function normalizarSexo(sexo: string | null | undefined): 'M' | 'F' | null {
  const s = (sexo ?? '').trim().toUpperCase();
  if (s.startsWith('M')) return 'M';
  if (s.startsWith('F')) return 'F';
  return null;
}

/**
 * Sem sexo conhecido, junta as duas variantes preservando o prefixo comum:
 * `sozinho|sozinha` → `sozinho(a)`, `Querido|Querida` → `Querido(a)`, `ele|ela` → `ele(a)`.
 */
function juntarVariantes(masculino: string, feminino: string): string {
  let i = 0;
  while (i < masculino.length && i < feminino.length && masculino[i] === feminino[i]) i++;
  const prefixo = masculino.slice(0, i);
  return `${prefixo}${masculino.slice(i)}(${feminino.slice(i)})`;
}

/** Aplica as variáveis e a flexão de gênero ao template. */
export function renderizarMensagemFaltas(template: string, ctx: ContextoMensagemFaltas): string {
  const sexo = normalizarSexo(ctx.sexo);

  const comGenero = (template ?? '').replace(
    /\{([^{}|]*)\|([^{}|]*)\}/g,
    (_todo, m: string, f: string) => {
      if (sexo === 'M') return m;
      if (sexo === 'F') return f;
      return juntarVariantes(m, f);
    },
  );

  const valores: Record<string, string> = {
    primeiroNome: primeiroNome(ctx.nome),
    nome: (ctx.nome ?? '').trim(),
    turma: (ctx.turma ?? '').trim(),
    faltas: ctx.faltas != null ? String(ctx.faltas) : '',
    ultimaData: (ctx.ultimaData ?? '').trim(),
  };

  return comGenero.replace(/\{(\w+)\}/g, (todo, chave: string) =>
    Object.prototype.hasOwnProperty.call(valores, chave) ? valores[chave]! : todo,
  );
}

/**
 * Telefone gravado (com ou sem máscara) no formato que o `wa.me` aceita: só dígitos, com DDI.
 * Devolve `null` quando não dá para montar um número plausível — é o que faz o botão de
 * WhatsApp não aparecer para quem não tem celular utilizável.
 */
export function normalizarNumeroWhatsapp(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let d = raw.replace(/\D/g, '');
  if (!d) return null;
  d = d.replace(/^0+/, '');
  if (d.length < 10) return null;
  if (!d.startsWith('55') && (d.length === 10 || d.length === 11)) {
    d = '55' + d;
  }
  if (d.length < 12 || d.length > 15) return null;
  return d;
}

/** `https://wa.me/{telefone}?text=…`, ou `null` sem celular utilizável. */
export function linkWhatsapp(telefone: string | null | undefined, texto: string): string | null {
  const num = normalizarNumeroWhatsapp(telefone);
  return num ? `https://wa.me/${num}?text=${encodeURIComponent(texto)}` : null;
}
